import React from 'react';
import { createPortal } from 'react-dom';
import { X, ImageOff, CalendarCheck, DollarSign, TrendingUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Personagem } from '../../../types/personagem';
import { PersonagemStats } from '../../../utils/personagemStats';
import { formatCurrency } from '../../../utils/currencyUtils';
import { formatDate } from '../../../utils/dateUtils';

interface PersonagemDetailModalProps {
  personagem: Personagem | null;
  stats: PersonagemStats | null;
  loadingStats: boolean;
  onClose: () => void;
  onImageClick: (imageUrl: string) => void;
}

const StatBlock: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({
  label,
  value,
  icon
}) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase mb-1">
      {icon}
      {label}
    </div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
  </div>
);

export const PersonagemDetailModal: React.FC<PersonagemDetailModalProps> = ({
  personagem,
  stats,
  loadingStats,
  onClose,
  onImageClick
}) => {
  const navigate = useNavigate();

  if (!personagem) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-[fadeIn_0.1s_ease-out_forwards]" onClick={onClose}>
      <div
        className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-[slideInFromBottom2_0.15s_ease-out_forwards] animate-[zoomIn95_0.15s_ease-out_forwards]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/30"></div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => personagem.fotoUrl && onImageClick(personagem.fotoUrl)}
                className="h-14 w-14 rounded-xl bg-white/20 border border-white/30 overflow-hidden flex items-center justify-center shrink-0"
                title={personagem.fotoUrl ? 'Ampliar foto' : undefined}
              >
                {personagem.fotoUrl ? (
                  <img src={personagem.fotoUrl} alt={personagem.nome} className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="w-6 h-6 text-white/70" />
                )}
              </button>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white truncate">{personagem.nome}</h2>
                <p className="text-sm text-teal-100 truncate">{personagem.categoria || 'Sem categoria'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {personagem.descricao && (
            <p className="text-sm text-slate-600">{personagem.descricao}</p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBlock
              label="Preço base"
              value={personagem.precoBase ? formatCurrency(personagem.precoBase) : 'A definir'}
              icon={<DollarSign className="w-4 h-4" />}
            />
            <StatBlock
              label="Contratos usado"
              value={loadingStats ? '...' : String(stats?.totalEventos || 0)}
              icon={<CalendarCheck className="w-4 h-4" />}
            />
            <StatBlock
              label="Receita gerada"
              value={loadingStats ? '...' : formatCurrency(stats?.receitaTotal || 0)}
              icon={<TrendingUp className="w-4 h-4" />}
            />
            <StatBlock
              label="Receita média/evento"
              value={loadingStats ? '...' : formatCurrency(stats?.receitaMedia || 0)}
              icon={<TrendingUp className="w-4 h-4" />}
            />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Eventos em que foi usado</h3>

            {loadingStats ? (
              <p className="text-sm text-slate-400">Carregando eventos...</p>
            ) : stats && stats.eventos.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {stats.eventos.map((evento) => (
                  <div
                    key={evento.contractId}
                    className="flex items-center justify-between gap-3 text-sm bg-slate-50 border border-slate-100 rounded-lg p-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{evento.contratante}</p>
                      <p className="text-xs text-slate-500">
                        {evento.dataFesta ? formatDate(evento.dataFesta) : 'Data não informada'}
                        {evento.valorPersonagem > 0 && ` • Personagem: ${formatCurrency(evento.valorPersonagem)}`}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/admin/contratos/${evento.contractId}`)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-teal-700 hover:text-teal-900 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors shrink-0"
                      title="Ver contrato"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Ver
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">
                Esse personagem ainda não foi usado em nenhum orçamento com "Quais Personagens?" preenchido.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
